/**
 * Flowstate Redux Slice
 * Manages state for interview prep catalog, user progress, and access control
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as flowstateApi from "./flowstateApi";

// ─── Async Thunks ────────────────────────────────────────────────────────────

// Catalog thunks
export const fetchCatalog = createAsyncThunk(
  "flowstate/fetchCatalog",
  async (_, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchCatalog();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTracks = createAsyncThunk(
  "flowstate/fetchTracks",
  async (_, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchTracks();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTrackDetails = createAsyncThunk(
  "flowstate/fetchTrackDetails",
  async (trackId, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchTrackDetails(trackId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategoryDetails = createAsyncThunk(
  "flowstate/fetchCategoryDetails",
  async (categoryId, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchCategoryDetails(categoryId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTopicDetails = createAsyncThunk(
  "flowstate/fetchTopicDetails",
  async (topicId, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchTopicDetails(topicId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Progress thunks
export const fetchUserProgress = createAsyncThunk(
  "flowstate/fetchUserProgress",
  async (_, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchUserProgress();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markProblemComplete = createAsyncThunk(
  "flowstate/markProblemComplete",
  async ({ topicId, problemId }, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.markProblemComplete(topicId, problemId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Access thunks
export const fetchUserAccess = createAsyncThunk(
  "flowstate/fetchUserAccess",
  async (_, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.fetchUserAccess();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkTopicAccess = createAsyncThunk(
  "flowstate/checkTopicAccess",
  async (topicId, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.checkTopicAccess(topicId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Payment thunks
export const createPaymentOrder = createAsyncThunk(
  "flowstate/createPaymentOrder",
  async ({ contentType, objectId }, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.createPaymentOrder(contentType, objectId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyPayment = createAsyncThunk(
  "flowstate/verifyPayment",
  async ({ razorpayOrderId, razorpayPaymentId, razorpaySignature }, { rejectWithValue }) => {
    try {
      const data = await flowstateApi.verifyPayment(
        razorpayOrderId,
        razorpayPaymentId,
        razorpaySignature
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  // Catalog
  tracks: [],
  categories: [],
  catalogLoading: false,
  catalogError: null,

  // Current track/category/topic details
  currentTrack: null,
  currentCategory: null,
  currentTopic: null,
  detailsLoading: false,
  detailsError: null,

  // User access (purchased tracks and categories)
  purchasedTracks: [],
  purchasedCategories: [],
  accessLoading: false,
  accessError: null,

  // User progress
  progress: [],
  progressLoading: false,
  progressError: null,

  // Current topic access check
  topicAccess: null,
  topicAccessLoading: false,

  // Payment state
  paymentOrder: null,
  paymentLoading: false,
  paymentError: null,
  paymentSuccess: false,

  // UI state
  selectedTrackId: null,
  selectedCategoryId: null,
  selectedTopicId: null,
  selectedProblemIndex: 0,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const flowstateSlice = createSlice({
  name: "flowstate",
  initialState,
  reducers: {
    setSelectedTrack: (state, action) => {
      state.selectedTrackId = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategoryId = action.payload;
    },
    setSelectedTopic: (state, action) => {
      state.selectedTopicId = action.payload;
    },
    setSelectedProblemIndex: (state, action) => {
      state.selectedProblemIndex = action.payload;
    },
    clearPaymentState: (state) => {
      state.paymentOrder = null;
      state.paymentLoading = false;
      state.paymentError = null;
      state.paymentSuccess = false;
    },
    clearErrors: (state) => {
      state.catalogError = null;
      state.detailsError = null;
      state.accessError = null;
      state.progressError = null;
      state.paymentError = null;
    },
    resetFlowstateState: () => initialState,
  },
  extraReducers: (builder) => {
    // ─── Fetch Catalog ──────────────────────────────────────────────────────
    builder
      .addCase(fetchCatalog.pending, (state) => {
        state.catalogLoading = true;
        state.catalogError = null;
      })
      .addCase(fetchCatalog.fulfilled, (state, action) => {
        state.catalogLoading = false;
        state.tracks = action.payload.tracks || [];
        state.categories = action.payload.categories || [];
      })
      .addCase(fetchCatalog.rejected, (state, action) => {
        state.catalogLoading = false;
        state.catalogError = action.payload;
      });

    // ─── Fetch Tracks ───────────────────────────────────────────────────────
    builder
      .addCase(fetchTracks.pending, (state) => {
        state.catalogLoading = true;
        state.catalogError = null;
      })
      .addCase(fetchTracks.fulfilled, (state, action) => {
        state.catalogLoading = false;
        state.tracks = action.payload;
      })
      .addCase(fetchTracks.rejected, (state, action) => {
        state.catalogLoading = false;
        state.catalogError = action.payload;
      });

    // ─── Fetch Track Details ────────────────────────────────────────────────
    builder
      .addCase(fetchTrackDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchTrackDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.currentTrack = action.payload;
      })
      .addCase(fetchTrackDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      });

    // ─── Fetch Category Details ─────────────────────────────────────────────
    builder
      .addCase(fetchCategoryDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchCategoryDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.currentCategory = action.payload;
      })
      .addCase(fetchCategoryDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      });

    // ─── Fetch Topic Details ────────────────────────────────────────────────
    builder
      .addCase(fetchTopicDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchTopicDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.currentTopic = action.payload;
      })
      .addCase(fetchTopicDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      });

    // ─── Fetch User Access ──────────────────────────────────────────────────
    builder
      .addCase(fetchUserAccess.pending, (state) => {
        state.accessLoading = true;
        state.accessError = null;
      })
      .addCase(fetchUserAccess.fulfilled, (state, action) => {
        state.accessLoading = false;
        state.purchasedTracks = action.payload.purchased_tracks || [];
        state.purchasedCategories = action.payload.purchased_categories || [];
      })
      .addCase(fetchUserAccess.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessError = action.payload;
      });

    // ─── Check Topic Access ─────────────────────────────────────────────────
    builder
      .addCase(checkTopicAccess.pending, (state) => {
        state.topicAccessLoading = true;
      })
      .addCase(checkTopicAccess.fulfilled, (state, action) => {
        state.topicAccessLoading = false;
        state.topicAccess = action.payload;
      })
      .addCase(checkTopicAccess.rejected, (state) => {
        state.topicAccessLoading = false;
        state.topicAccess = { has_access: false };
      });

    // ─── Fetch User Progress ────────────────────────────────────────────────
    builder
      .addCase(fetchUserProgress.pending, (state) => {
        state.progressLoading = true;
        state.progressError = null;
      })
      .addCase(fetchUserProgress.fulfilled, (state, action) => {
        state.progressLoading = false;
        state.progress = action.payload;
      })
      .addCase(fetchUserProgress.rejected, (state, action) => {
        state.progressLoading = false;
        state.progressError = action.payload;
      });

    // ─── Mark Problem Complete ──────────────────────────────────────────────
    builder
      .addCase(markProblemComplete.fulfilled, (state, action) => {
        // Update progress in the current topic if loaded
        if (state.currentTopic && state.currentTopic.topic_id === action.payload.topic_id) {
          // Find and update the progress
          const existingProgress = state.progress.find(
            (p) => p.topic_id === action.payload.topic_id
          );
          if (existingProgress) {
            existingProgress.completed_count = action.payload.completed_count;
            existingProgress.progress_percentage = action.payload.progress_percentage;
          }
        }
      });

    // ─── Create Payment Order ───────────────────────────────────────────────
    builder
      .addCase(createPaymentOrder.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
      })
      .addCase(createPaymentOrder.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentOrder = action.payload;
      })
      .addCase(createPaymentOrder.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      });

    // ─── Verify Payment ─────────────────────────────────────────────────────
    builder
      .addCase(verifyPayment.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.paymentSuccess = true;
        // Clear payment order after successful verification
        state.paymentOrder = null;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload;
      });
  },
});

export const {
  setSelectedTrack,
  setSelectedCategory,
  setSelectedTopic,
  setSelectedProblemIndex,
  clearPaymentState,
  clearErrors,
  resetFlowstateState,
} = flowstateSlice.actions;

export default flowstateSlice.reducer;
